import schedulePlanAPI, {
  SchedulePlan,
  SchedulePlanProcessingNotification,
} from "apis/SchedulePlanAPI";
import { onValue, ref } from "firebase/database";
import toast from "react-hot-toast";
import { getFirebaseRealtimeDatabase } from "./realtimeDatabase";

export type SchedulePlanProcessingPhase =
  | "accepted"
  | "processing"
  | "completed"
  | "error";

export interface SchedulePlanProcessingEventDetail {
  schedulePlanId: string;
  phase: SchedulePlanProcessingPhase;
  schedulePlan?: SchedulePlan;
  processingNotification: SchedulePlanProcessingNotification | null;
  message?: string;
}

export const SCHEDULE_PLAN_PROCESSING_EVENT =
  "schedule-plan-processing-updated";

const activeMonitors = new Map<string, () => void>();

const normalizePath = (path: string): string =>
  path.replace(/^\/+|\/+$/g, "");

const buildToastId = (schedulePlanId: string): string =>
  `schedule-plan-processing-${schedulePlanId}`;

const emitSchedulePlanProcessingEvent = (
  detail: SchedulePlanProcessingEventDetail
) => {
  window.dispatchEvent(
    new CustomEvent<SchedulePlanProcessingEventDetail>(
      SCHEDULE_PLAN_PROCESSING_EVENT,
      { detail }
    )
  );
};

const cleanupMonitor = (schedulePlanId: string) => {
  const stop = activeMonitors.get(schedulePlanId);

  if (stop) {
    stop();
    activeMonitors.delete(schedulePlanId);
  }
};

export const stopSchedulePlanProcessingMonitor = (schedulePlanId: string) => {
  cleanupMonitor(schedulePlanId);
  toast.dismiss(buildToastId(schedulePlanId));
};

export const startSchedulePlanProcessingMonitor = ({
  schedulePlanId,
  processingNotification,
}: {
  schedulePlanId: string;
  processingNotification: SchedulePlanProcessingNotification | null;
}) => {
  cleanupMonitor(schedulePlanId);

  emitSchedulePlanProcessingEvent({
    schedulePlanId,
    phase: "accepted",
    processingNotification,
  });

  const toastId = buildToastId(schedulePlanId);

  if (!processingNotification?.path) {
    toast.success("Plano recorrente criado. Acompanhe o status na listagem.", {
      id: toastId,
    });
    return () => stopSchedulePlanProcessingMonitor(schedulePlanId);
  }

  const database = getFirebaseRealtimeDatabase();

  if (!database) {
    toast.success(
      "Plano recorrente criado. A integracao em tempo real nao esta configurada neste ambiente.",
      { id: toastId }
    );
    return () => stopSchedulePlanProcessingMonitor(schedulePlanId);
  }

  let finished = false;

  const finalize = async () => {
    if (finished) {
      return;
    }

    finished = true;

    try {
      const response = await schedulePlanAPI.getById(schedulePlanId);
      const schedulePlan = response.data;

      if (schedulePlan.status === "ERROR") {
        const message =
          schedulePlan.userMessage ||
          "O plano recorrente terminou com erro no processamento.";

        toast.error(message, { id: toastId });
        emitSchedulePlanProcessingEvent({
          schedulePlanId,
          phase: "error",
          processingNotification,
          schedulePlan,
          message,
        });
      } else {
        const message = "Plano recorrente concluido com sucesso!";
        toast.success(message, { id: toastId });
        emitSchedulePlanProcessingEvent({
          schedulePlanId,
          phase: "completed",
          processingNotification,
          schedulePlan,
          message,
        });
      }
    } catch (error) {
      const message =
        "Nao foi possivel confirmar o status final do plano recorrente.";
      toast.error(message, { id: toastId });
      emitSchedulePlanProcessingEvent({
        schedulePlanId,
        phase: "error",
        processingNotification,
        message,
      });
    } finally {
      cleanupMonitor(schedulePlanId);
    }
  };

  toast.loading("Plano recorrente em processamento...", { id: toastId });
  emitSchedulePlanProcessingEvent({
    schedulePlanId,
    phase: "processing",
    processingNotification,
  });

  let currentUnsubscribe = () => {};
  activeMonitors.set(schedulePlanId, () => currentUnsubscribe());

  currentUnsubscribe = onValue(
    ref(database, normalizePath(processingNotification.path)),
    async (snapshot) => {
      const payload = snapshot.val() as { ongoingBatchAction?: boolean } | null;

      if (payload?.ongoingBatchAction) {
        emitSchedulePlanProcessingEvent({
          schedulePlanId,
          phase: "processing",
          processingNotification,
        });
        return;
      }

      await finalize();
    },
    () => {
      const message =
        "Falha ao acompanhar o processamento do plano recorrente em tempo real.";
      toast.error(message, { id: toastId });
      emitSchedulePlanProcessingEvent({
        schedulePlanId,
        phase: "error",
        processingNotification,
        message,
      });
      cleanupMonitor(schedulePlanId);
    }
  );

  return () => stopSchedulePlanProcessingMonitor(schedulePlanId);
};
